'use client';

import { useTranslations, useLocale } from 'next-intl';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { alpha, useTheme } from '@mui/material/styles';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { trackCTAClick } from '../../../lib/analytics';
import { getSampleBotUrl } from '../../../lib/sampleBots';

const useCaseItems = [
  { key: 'tournaments', icon: EmojiEventsIcon },
  { key: 'workshops', icon: SchoolIcon },
  { key: 'conferences', icon: GroupsIcon },
  { key: 'festivals', icon: CelebrationIcon },
] as const;

export function UseCasesSection() {
  const t = useTranslations('creator.eventFaqBot.useCases');
  const locale = useLocale();
  const theme = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          component="h2"
          fontWeight={700}
          textAlign="center"
          sx={{ mb: { xs: 4, md: 6 } }}
        >
          {t('title')}
        </Typography>
        <Grid container spacing={3}>
          {useCaseItems.map(({ key, icon: Icon }) => (
            <Grid key={key} size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '100%',
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Icon sx={{ fontSize: 28, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {t(key)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`${key}Desc`)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {/* Live example CTA (Story 19.51) - lets visitors chat with a real bot */}
        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
          <Button
            component={MuiLink}
            href={getSampleBotUrl('events', locale)}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon />}
            onClick={() => trackCTAClick('use_case_live_example', 'event_faq_bot_use_cases', 'events')}
            sx={{ textTransform: 'none' }}
          >
            {t('liveExampleCta')}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
